import React from 'react';
import { Box } from '@mui/material';
import { Switch, Route, Redirect, useRouteMatch, useLocation } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { NAV_MODULES, PRE_AUTH_TAB_ENABLED, resolveActiveNavId, findNavItem } from '../../navigation';
import { fadeInSx } from './pharmacyStyles';

import PharmacyOverview from './PharmacyOverview';
import ActiveDispensesTab from './tabs/ActiveDispensesTab';
import ControlledQueueTab from './tabs/ControlledQueueTab';
import ControlledOrdersTab from './tabs/ControlledOrdersTab';
import ControlledOrderDetail from './ControlledOrderDetail';
import BillsTab from './tabs/BillsTab';
import PreAuthTab from './tabs/PreAuthTab';
import WardReturnsTab from './tabs/WardReturnsTab';
import DirectDispensesTab from './tabs/DirectDispensesTab';
import RefundsQueueTab from './tabs/RefundsQueueTab';
import OpdDispenseFlow from './opd/OpdDispenseFlow';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';

import StartDispensePage from './StartDispensePage';
import DispenseForm from './DispenseForm';
import DispenseList from './DispenseList';
import DispenseDetail from './DispenseDetail';
import PharmacyReturn from './PharmacyReturn';

/**
 * Detail and wizard flows own their whole viewport and render their own heading.
 * (Previously this also hid the horizontal tab bar, which the module sidebar replaced.)
 */
function hidesPageHeading(url, pathname) {
  const relative = pathname.slice(url.length).replace(/^\//, '');
  return (
    relative.includes('opd-dispenses/dispense') ||
    relative.includes('direct-dispenses/dispense') ||
    relative.startsWith('start-dispense/') ||
    relative.startsWith('dispense/') ||
    /^dispenses\/[^/]+/.test(relative) ||
    relative.startsWith('return/')
  );
}

/**
 * The tab pages render no heading of their own — the removed tab bar's static
 * "Pharmacy Management" header was their only one. Derive the title from the active
 * sidebar item so each list page is named for what it actually shows.
 */
function PharmacySection({ children }) {
  const { url } = useRouteMatch();
  const location = useLocation();
  const activeId = resolveActiveNavId(NAV_MODULES.pharmacy, url, location.pathname);
  const item = findNavItem(NAV_MODULES.pharmacy, activeId);
  const showHeading =
    item && item.path !== '' && !hidesPageHeading(url, location.pathname);

  return (
    <Box sx={fadeInSx}>
      {showHeading && (
        <PageHeader breadcrumb={['Inventory Suite', 'Pharmacy Management']} title={item.label} />
      )}
      {children}
    </Box>
  );
}

function RoleGate({ allow, loading, children, fallbackPath }) {
  const { url } = useRouteMatch();
  if (loading) {
    return null;
  }
  if (!allow) {
    return <Redirect to={fallbackPath || `${url}/active-dispenses`} />;
  }
  return children;
}

function DirectModeRedirect({ anonymous }) {
  const { url } = useRouteMatch();
  const location = useLocation();
  const base = url
    .replace(/\/quick-register\/?$/, '')
    .replace(/\/walk-in\/?$/, '')
    .replace(/\/direct\/?$/, '');
  // Carry the incoming query through. Building a fresh URLSearchParams here
  // silently dropped every other param -- including the saleId that the
  // Controlled Queue puts on its resume link, which is why a parked order
  // could never be reopened.
  const params = new URLSearchParams(location.search);
  params.set('mode', 'direct');
  if (anonymous === true) params.set('anonymous', '1');
  if (anonymous === false) params.set('anonymous', '0');
  return <Redirect to={`${base}?${params.toString()}`} />;
}

function OpdDispensePathRedirect() {
  const { url } = useRouteMatch();
  const location = useLocation();
  const target = url.replace(/\/opd-dispenses\/dispense$/, '/direct-dispenses/dispense');
  return <Redirect to={`${target}${location.search || ''}`} />;
}

export default function PharmacyShell() {
  const { path, url } = useRouteMatch();
  const { can, loading: permissionsLoading } = useInvRole();

  return (
    <PharmacySection>
      <Switch>
        <Route exact path={path} component={PharmacyOverview} />

        <Route exact path={`${path}/active-dispenses`} component={ActiveDispensesTab} />
        <Route
          exact
          path={`${path}/start-dispense/:prescriptionId`}
          render={() => (
            <RoleGate allow={can(INV_ACTIONS.DISPENSE_START)} loading={permissionsLoading}>
              <StartDispensePage />
            </RoleGate>
          )}
        />
        <Route exact path={`${path}/controlled-queue`} component={ControlledQueueTab} />
        <Route exact path={`${path}/controlled-orders`} component={ControlledOrdersTab} />
        <Route exact path={`${path}/controlled-orders/:saleId`} component={ControlledOrderDetail} />
        <Route exact path={`${path}/clinical-alerts`}>
          <Redirect to={`${url}/active-dispenses`} />
        </Route>
        <Route exact path={`${path}/bills`} component={BillsTab} />
        <Route exact path={`${path}/refunds`} component={RefundsQueueTab} />
        {PRE_AUTH_TAB_ENABLED ? (
          <Route exact path={`${path}/pre-auth`} component={PreAuthTab} />
        ) : (
          <Route exact path={`${path}/pre-auth`}>
            <Redirect to={`${url}/active-dispenses`} />
          </Route>
        )}
        <Route exact path={`${path}/ward-returns`} component={WardReturnsTab} />

        <Route exact path={`${path}/direct-dispenses`} component={DirectDispensesTab} />
        <Route exact path={`${path}/direct-dispenses/dispense`} component={OpdDispenseFlow} />
        <Route exact path={`${path}/direct-dispenses/direct`}>
          <DirectModeRedirect />
        </Route>
        <Route exact path={`${path}/direct-dispenses/quick-register`}>
          <DirectModeRedirect anonymous={false} />
        </Route>
        <Route exact path={`${path}/direct-dispenses/walk-in`}>
          <DirectModeRedirect anonymous />
        </Route>

        <Route exact path={`${path}/opd-dispenses`}>
          <Redirect to={`${url}/direct-dispenses?mode=existing`} />
        </Route>
        <Route exact path={`${path}/opd-dispenses/dispense`} component={OpdDispensePathRedirect} />

        <Route exact path={`${path}/dispense/new`} component={DispenseForm} />
        <Route exact path={`${path}/dispenses/:id`} component={DispenseDetail} />
        <Route exact path={`${path}/return/new`} component={PharmacyReturn} />

        {/* Was a redirect to active-dispenses, which left an in-flight dispense
            reachable only by its /dispenses/:id url — so a pharmacist who stepped
            away to the Controlled Queue had no way back and restarted the order. */}
        <Route exact path={`${path}/dispenses`} component={DispenseList} />
        <Route exact path={`${path}/billing`}>
          <Redirect to={`${url}/bills`} />
        </Route>

        <Redirect to={`${url}/active-dispenses`} />
      </Switch>
    </PharmacySection>
  );
}
